import { Injectable } from '@angular/core'
import { HearingSection } from '@ftr/annotations-contracts'
import {
  CourtroomSessionsService,
  isOnTheRecordByDeviceClock,
  isOnTheRecordBySegments,
  isTheRecorderRunning,
  RealTimeLiveStreamService,
} from '@ftr/api-regional'
import { PublicConfiguration } from '@ftr/contracts/api/configuration'
import { Timeframe } from '@ftr/contracts/api/shared'
import { RecordingSegment, RecordingWithSegments } from '@ftr/contracts/read'
import { CourtroomSession, ProducerState, WeeklyStreamLocationConfiguration } from '@ftr/contracts/regional-api'
import { collectFirst } from '@ftr/contracts/shared/ArrayUtils'
import { LocalTimeRange, Uuid } from '@ftr/contracts/type/shared'
import { CoreConfigurationService } from '@ftr/data-court-system'
import { LocationService, LocationWithParent } from '@ftr/data-location'
import { WeeklyStreamLocationConfigurationService } from '@ftr/data-realtime-playback'
import { ApiResult, RemoteData, Success, tapData, ToastService, unwrapData } from '@ftr/foundation'
import { OnRecordState } from '@ftr/stt-search'
import { LoggingService } from '@ftr/ui-observability'
import { ProducerDetailWithLocation, ProducersAugmentedService } from '@ftr/ui-realtime-recorder'
import { VocabularyTermsService } from '@ftr/ui-vocab'
import { LocalTime, ZoneId } from '@js-joda/core'
import { Action, Selector, State, StateContext } from '@ngxs/store'
import { patch } from '@ngxs/store/operators'
import { filter, firstValueFrom, map, Observable, of, switchMap, tap } from 'rxjs'
import { ManageCourtroomHearingsService } from '../../services'
import { ManageCourtroomContext } from '../../types'
import {
  FetchActiveHearingSection,
  FetchCourtroomRecordingSessions,
  GetCourtroom,
  GetCourtroomRecording,
  ResetCourtRecording,
  ResetCourtroom,
  SetManageCourtroomInfo,
  SetRecordingTimezone,
  StartCourtroomHearingAction,
  StopCourtroomHearingAction,
  UpdateCourtroomHearingAction,
} from './manage-courtroom.actions'
import { ManageCourtroomStateModel } from './manage-courtroom.model'

export function defaultManageCourtroomState(): ManageCourtroomStateModel {
  return {
    recordingId: undefined,
    courtroomId: undefined,
    courtroomName: undefined,
    courtSystemId: undefined,
    activeHearingSection: undefined,
    courtRecording: undefined,
    onRecordState: OnRecordState.NotRecording,
    liveSealing: false,
    timezone: undefined,
    manageCourtroomContext: undefined,
    sealedTimeframes: undefined,
    weeklyStreamLocationConfiguration: undefined,
    courtroomSessions: [],
    realTimeRecorder: undefined,
  }
}

export const STOPPING_SESSION_TIMEOUT = 5000

const recordingNotAsked: RemoteData<RecordingWithSegments> = RemoteData.notAsked()

@State<ManageCourtroomStateModel>({
  name: 'manageCourtroomState',
  defaults: defaultManageCourtroomState(),
})
@Injectable()
export class ManageCourtroomState {
  constructor(
    private readonly loggingService: LoggingService,
    private readonly coreConfigurationService: CoreConfigurationService,
    private readonly courtroomSessionsService: CourtroomSessionsService,
    private readonly manageCourtroomHearingsService: ManageCourtroomHearingsService,
    private readonly realTimeLiveStreamService: RealTimeLiveStreamService,
    private readonly toastService: ToastService,
    private readonly vocabService: VocabularyTermsService,
    private readonly locationService: LocationService,
    private readonly weeklyStreamLocationConfigurationService: WeeklyStreamLocationConfigurationService,
    private readonly realTimeRecorderService: ProducersAugmentedService,
  ) {}

  @Selector()
  static activeHearingSection(state: ManageCourtroomStateModel): HearingSection | undefined {
    return state.activeHearingSection
  }

  @Selector()
  static timezone(state: ManageCourtroomStateModel): ZoneId | undefined {
    return state.timezone
  }

  @Selector()
  static liveSealing(state: ManageCourtroomStateModel): boolean {
    return state.liveSealing
  }

  @Selector()
  static onRecordState(state: ManageCourtroomStateModel): OnRecordState {
    return state.onRecordState
  }

  @Selector()
  static courtroomId(state: ManageCourtroomStateModel): Uuid | undefined {
    return state.courtroomId
  }

  @Selector()
  static courtroomName(state: ManageCourtroomStateModel): Uuid | undefined {
    return state.courtroomName
  }

  @Selector()
  static courtSystemId(state: ManageCourtroomStateModel): Uuid | undefined {
    return state.courtSystemId
  }

  @Selector()
  static courtRecording(state: ManageCourtroomStateModel): RemoteData<RecordingWithSegments | undefined> {
    return state.courtRecording || recordingNotAsked
  }

  @Selector()
  static manageCourtroomContext(state: ManageCourtroomStateModel): ManageCourtroomContext | undefined {
    return state.manageCourtroomContext
  }

  @Selector()
  static sealedTimeframes(state: ManageCourtroomStateModel): Timeframe[] | undefined {
    return state.sealedTimeframes
  }

  @Selector()
  static weeklyStreamLocationConfiguration(
    state: ManageCourtroomStateModel,
  ): WeeklyStreamLocationConfiguration | undefined {
    return state.weeklyStreamLocationConfiguration
  }

  @Selector()
  static realTimeRecorder(state: ManageCourtroomStateModel): ProducerDetailWithLocation | undefined {
    return state.realTimeRecorder
  }

  @Action(SetManageCourtroomInfo)
  setManageCourtroomInfo(
    { patchState }: StateContext<ManageCourtroomStateModel>,
    { courtSystemId, recordingId, manageCourtroomContext }: SetManageCourtroomInfo,
  ): void {
    patchState({
      courtSystemId,
      recordingId,
      manageCourtroomContext,
    })
  }

  @Action(SetRecordingTimezone)
  setRecordingTimezone(
    { patchState }: StateContext<ManageCourtroomStateModel>,
    { recordingTimezone }: SetRecordingTimezone,
  ): void {
    patchState({ timezone: recordingTimezone })
  }

  @Action(ResetCourtRecording)
  resetCourtRecording({ patchState }: StateContext<ManageCourtroomStateModel>): void {
    patchState({
      courtRecording: undefined,
      onRecordState: undefined,
    })
  }

  @Action(ResetCourtroom)
  resetCourtroom({ patchState }: StateContext<ManageCourtroomStateModel>): void {
    patchState({
      timezone: undefined,
      courtroomId: undefined,
      courtroomName: undefined,
      courtSystemId: undefined,
      weeklyStreamLocationConfiguration: undefined,
      manageCourtroomContext: undefined,
      realTimeRecorder: undefined,
      activeHearingSection: undefined,
    })
  }

  @Action(FetchActiveHearingSection)
  async fetchActiveHearingSection(
    { setState, getState }: StateContext<ManageCourtroomStateModel>,
    { courtSystemId, courtroomId }: FetchActiveHearingSection,
  ): Promise<void> {
    const recordingId = getState().recordingId
    const stateCourtroomId = getState().courtroomId
    if (!recordingId || stateCourtroomId !== courtroomId) {
      setState(patch({ activeHearingSection: undefined }))
      return
    }

    const sections = await this.manageCourtroomHearingsService.getSections(courtSystemId, recordingId)
    if (sections.status === 200) {
      const activeHearingSection = sections.body.find(hearing => isOngoing(LocalTime.now(), hearing))
      setState(patch({ activeHearingSection }))
    } else {
      setState(patch({ activeHearingSection: undefined }))
    }
  }

  private recordingRecorderOrFirst(
    realTimeRecorders: ProducerDetailWithLocation[],
  ): ProducerDetailWithLocation | undefined {
    if (realTimeRecorders.length <= 0) return undefined
    const recordingStates = [ProducerState.InSession, ProducerState.Starting, ProducerState.Configuring]
    const recordingRecorder = realTimeRecorders.find(recorder => recordingStates.includes(recorder.state))
    if (recordingRecorder) return recordingRecorder
    const firstEnabledConnectedRecorder = realTimeRecorders.find(
      recorder => recorder.state !== ProducerState.Disabled && recorder.state !== ProducerState.Disconnected,
    )
    if (firstEnabledConnectedRecorder) return firstEnabledConnectedRecorder
    const firstEnabledRecorder = realTimeRecorders.find(recorder => recorder.state !== ProducerState.Disabled)
    if (firstEnabledRecorder) return firstEnabledRecorder
    const firstConnectedRecorder = realTimeRecorders.find(recorder => recorder.state !== ProducerState.Disconnected)
    if (firstConnectedRecorder) return firstConnectedRecorder
    return realTimeRecorders[0]
  }

  @Action(GetCourtroom)
  getCourtroom(
    { patchState }: StateContext<ManageCourtroomStateModel>,
    { locationId, courtSystemId }: GetCourtroom,
  ): Observable<
    [
      LocationWithParent | undefined,
      PublicConfiguration,
      WeeklyStreamLocationConfiguration,
      ProducerDetailWithLocation[] | undefined,
    ]
  > {
    return ApiResult.combine([
      this.locationService.getCourtroom(locationId),
      this.coreConfigurationService.getByCourtSystem(courtSystemId),
      this.weeklyStreamLocationConfigurationService.get(courtSystemId, locationId),
      this.realTimeRecorderService.list(courtSystemId, locationId),
    ]).pipe(
      tapData(([locationWithParent, publicConfig, weeklySchedule, realTimeRecorders]) => {
        if (locationWithParent) {
          const { timeZoneId } = locationWithParent
          const timezone = timeZoneId || locationWithParent.parent.timeZoneId || publicConfig.timeZoneId
          const weeklyStreamLocationConfiguration = weeklySchedule
          // Select the first recorder if multiple are returned, as the system assumes only one active recorder per location.
          const realTimeRecorder = this.recordingRecorderOrFirst(realTimeRecorders)
          patchState({
            timezone,
            courtroomId: locationWithParent.id,
            courtroomName: locationWithParent.name,
            courtSystemId,
            weeklyStreamLocationConfiguration,
            realTimeRecorder,
          })
        }
      }),
      unwrapData(),
    )
  }

  @Action(FetchCourtroomRecordingSessions)
  fetchCourtroomRecordingSessions(
    { patchState }: StateContext<ManageCourtroomStateModel>,
    { courtSystemId, recordingId }: FetchCourtroomRecordingSessions,
  ): Observable<CourtroomSession[]> {
    return this.courtroomSessionsService.getRecordingCourtroomSessions(courtSystemId, recordingId).pipe(
      filter(remoteData => remoteData.isCompleted()),
      unwrapData(),
      tap(courtroomSessions => patchState({ courtroomSessions })),
    )
  }

  @Action(GetCourtroomRecording)
  getCourtroomRecording(
    { dispatch, patchState, getState }: StateContext<ManageCourtroomStateModel>,
    { recordingId, courtSystemId, force }: GetCourtroomRecording,
  ): Observable<void> {
    const recording = getState().courtRecording
    const hasRecording = recording?.isSuccess()
    const isDifferentRecording = recording?.data?.id !== recordingId
    if (hasRecording && !isDifferentRecording && !force) return of()

    return ApiResult.combine([
      this.coreConfigurationService.getByCourtSystem(courtSystemId),
      this.realTimeLiveStreamService.getRecordingWithCourtroomAndDepartment(courtSystemId, recordingId),
    ]).pipe(
      filter(remoteData => remoteData.isCompleted()),
      tap(remoteData => {
        remoteData.map(([configuration, courtRecording]) => {
          const timezone = calculateSupportedTimezone(courtRecording, configuration)
          const onRecordState = this.calculateOnRecordState(
            timezone,
            courtRecording.segments,
            courtRecording.onTheRecordTimeframes,
          )

          patchState({
            recordingId,
            courtroomId: courtRecording.courtroom?.id,
            courtroomName: courtRecording.courtroom?.name,
            courtRecording: new Success(courtRecording),
            timezone,
            onRecordState,
            sealedTimeframes: courtRecording.sealedSegments,
          })
        })
      }),
      unwrapData(),
      switchMap(() =>
        dispatch(new FetchCourtroomRecordingSessions(recordingId, courtSystemId)).pipe(map(() => undefined)),
      ),
    )
  }

  @Action(StartCourtroomHearingAction)
  async startCourtroomHearing(
    { setState, getState }: StateContext<ManageCourtroomStateModel>,
    action: StartCourtroomHearingAction,
  ): Promise<void> {
    const activeHearingSection = getState().activeHearingSection
    if (activeHearingSection) return

    const terms = await firstValueFrom(this.vocabService.observeTerms(action.courtSystemId))
    // Annotations can only be accurate to the second
    const startTime = action.startTime.withNano(0)

    const triggerResult = await this.manageCourtroomHearingsService.startBlankHearing(
      action.courtSystemId,
      action.locationId,
      startTime,
      action.recordingDate,
      action.courtroomSessionId,
    )

    if (triggerResult.status === 201) {
      setState(patch({ activeHearingSection: triggerResult.body }))
    } else {
      this.loggingService.error({
        message: 'Unexpected status code when creating hearing section',
        response: triggerResult,
      })
      this.toastService.error(`Failed to create ${terms.hearing.singular}.`)
    }
    return
  }

  @Action(StopCourtroomHearingAction)
  async stopCourtroomHearing(
    { setState, getState }: StateContext<ManageCourtroomStateModel>,
    action: StopCourtroomHearingAction,
  ): Promise<void> {
    const activeHearingSection = getState().activeHearingSection
    const liveLocalTime = LocalTime.now().withNano(0)
    if (!activeHearingSection || !liveLocalTime) return

    const updatedHearingSection: HearingSection = {
      ...activeHearingSection,
      endTime: liveLocalTime,
    }

    try {
      await this.manageCourtroomHearingsService.updateHearing(
        action.courtSystemId,
        action.locationId,
        updatedHearingSection,
        action.recordingDate,
        action.courtroomSessionId,
      )
      setState(patch({ activeHearingSection: undefined }))
    } catch (error) {
      this.loggingService.error({ message: 'Failed to update hearing section', error })
    }
  }

  @Action(UpdateCourtroomHearingAction)
  async updateCourtroomHearing(
    { setState, getState }: StateContext<ManageCourtroomStateModel>,
    action: UpdateCourtroomHearingAction,
  ): Promise<void> {
    // Get active hearing. If none exists, return.
    const activeHearingSection = getState().activeHearingSection
    if (!activeHearingSection) return

    // Prepare the updated hearing section
    const updatedHearingSection: HearingSection = {
      ...activeHearingSection,
      caseReference: action.hearingSection.caseReference?.trim() || activeHearingSection.caseReference,
      title: action.hearingSection.title?.trim() || activeHearingSection.title,
    }

    const triggerResult = await this.manageCourtroomHearingsService.updateHearing(
      action.courtSystemId,
      action.locationId,
      updatedHearingSection,
      action.recordingDate,
      action.courtroomSessionId,
    )

    if (triggerResult.status === 200) {
      if (triggerResult.body) {
        setState(patch({ activeHearingSection: triggerResult.body }))
      }
    } else {
      this.loggingService.error({ message: 'Failed to update hearing section', triggerResult })
    }
  }

  private calculateOnRecordState(
    timeZoneId: ZoneId | undefined,
    segments: RecordingSegment[],
    onTheRecordTimeframes: Timeframe[],
  ): OnRecordState {
    if (!isTheRecorderRunning(segments)) {
      return OnRecordState.NotRecording
    }

    // timeZoneId is not guaranteed for recordings produced by recorders up to v 0.29, hence an additional check using
    // recording segments. At the time of writing (Apr 2024) only one customer is left using an older recorder version
    const onRecord = timeZoneId
      ? isOnTheRecordByDeviceClock(onTheRecordTimeframes, timeZoneId)
      : isOnTheRecordBySegments(onTheRecordTimeframes, segments)

    return onRecord ? OnRecordState.OnRecord : OnRecordState.OffRecord
  }
}

export const isRecordingLive = (state: OnRecordState): boolean =>
  state === OnRecordState.OnRecord || state === OnRecordState.OffRecord

export function calculateSupportedTimezone(
  courtRecording: RecordingWithSegments,
  configuration: PublicConfiguration,
): ZoneId {
  const courtTimezone = courtRecording.courtroom?.courthouse?.timeZoneId ?? configuration.timeZoneId
  const recordingZoneId = collectFirst(courtRecording.segments, x => x.zoneId)

  // Check that this is supported by the moment timezones available, otherwise fallback to courtTimezone
  try {
    if (recordingZoneId && ZoneId.of(recordingZoneId.toString())) {
      return recordingZoneId
    }
    return courtTimezone
  } catch {
    return courtTimezone
  }
}

function isOngoing(
  liveLocalTime: LocalTime | undefined,
  hearingSection: Pick<HearingSection, 'startTime' | 'endTime'>,
): boolean {
  return liveLocalTime
    ? new LocalTimeRange(hearingSection.startTime, hearingSection.endTime).contains(liveLocalTime)
    : false
}
